import {useRef, useCallback} from 'react';
import {useThree} from '@react-three/fiber';
import * as THREE from 'three';

// Shared cache across all hook instances (one map model, same surface)
const sharedCache = new Map<string, number>();
let sharedMapMeshes: THREE.Object3D[] = [];
let sharedSceneVersion = 0;
const sharedRaycaster = new THREE.Raycaster();
// Reusable vectors to avoid allocations per raycast
const _rayOrigin = new THREE.Vector3();
const _rayDir = new THREE.Vector3(0, 0, -1); // Z-down (mine uses Z-up)

// Cache grid size — larger = fewer unique raycasts, slight position rounding
const CACHE_GRID = 5;

/**
 * Hook that raycasts downward (-Z) to find the mine model surface
 * and snaps asset positions onto the ramp/tunnel floor.
 * Mine coordinate system: X,Y horizontal, Z vertical (up).
 *
 * Uses a shared cache across all instances so raycasts are never duplicated.
 */
export function useGroundSnap(zOffset = 2) {
  const {scene} = useThree();
  const zOffsetRef = useRef(zOffset);
  zOffsetRef.current = zOffset;

  const snapToGround = useCallback(
    (x: number, y: number, z: number): {x: number; y: number; z: number} => {
      // Round to grid for cache key (X,Y are horizontal axes)
      const kx = Math.round(x / CACHE_GRID) * CACHE_GRID;
      const ky = Math.round(y / CACHE_GRID) * CACHE_GRID;
      const key = `${kx}_${ky}_${zOffsetRef.current}`;

      const cached = sharedCache.get(key);
      if (cached !== undefined) {
        return {x, y, z: cached};
      }

      // Rebuild mesh list only when scene structure changes
      const sceneVersion = scene.children.length;
      if (sceneVersion !== sharedSceneVersion) {
        sharedSceneVersion = sceneVersion;
        sharedMapMeshes = [];
        // Find map model directly from top-level children
        for (const child of scene.children) {
          if (child.userData.isMapModel) {
            child.traverse((obj: THREE.Object3D) => {
              if (obj instanceof THREE.Mesh) {
                sharedMapMeshes.push(obj);
              }
            });
            break; // Only one map model
          }
        }
      }

      if (sharedMapMeshes.length === 0) {
        return {x, y, z};
      }

      // Cast ray downward (-Z) from high above
      _rayOrigin.set(x, y, 10000);
      sharedRaycaster.set(_rayOrigin, _rayDir);

      const intersects = sharedRaycaster.intersectObjects(
        sharedMapMeshes,
        false,
      );

      if (intersects.length > 0) {
        const surfaceZ = intersects[0].point.z + zOffsetRef.current;
        sharedCache.set(key, surfaceZ);
        return {x, y, z: surfaceZ};
      }

      // No hit — return original position
      return {x, y, z};
    },
    [scene],
  );

  // Clear cache when model reloads
  const clearCache = useCallback(() => {
    sharedCache.clear();
    sharedSceneVersion = 0;
    sharedMapMeshes = [];
  }, []);

  return {snapToGround, clearCache};
}
